import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, Navigate, useParams, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getTruckRoute, getMultiStopRoute } from "@/lib/mapbox";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import type { PlaceData } from "@/components/ui/place-autocomplete";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

import DirectPostHeader from "@/components/lr-direct/DirectPostHeader";
import ShipmentDetailsCard from "@/components/post-load/ShipmentDetailsCard";
import EquipmentDetailsCard from "@/components/post-load/EquipmentDetailsCard";
import RouteMapCard from "@/components/post-load/RouteMapCard";
import DirectPublishCard, { POST_TYPE_META } from "@/components/lr-direct/DirectPublishCard";
import NetworkBrokerSelector, { type NetworkBrokerSelection } from "@/components/lr-direct/NetworkBrokerSelector";
import { StopCard, type Stop } from "@/components/post-load/StopCard";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";
import { bumpTemplateUsage } from "@/hooks/useLRDirectLoadTemplates";
import { useDefaultEquipment } from "@/hooks/lrDirect/useDefaultEquipment";
import { LRDirectLoadBriefCard, type LRDirectLoadBriefValue } from "@/components/lr-direct/LRDirectLoadBriefCard";

interface DirectFormData {
  origin: string;
  originPlaceData?: PlaceData;
  destination: string;
  destinationPlaceData?: PlaceData;
  pickupEarliest: string;
  deliveryDate: string;
  pickHrs: string;
  dropHrs: string;
  loadType: "FTL" | "LTL";
  loadNature: "spot" | "dedicated" | "contract";
  equipmentType: string;
  length: string;
  weight: string;
  commodity: string;
  referenceNumber: string;
  comments: string;
  totalMiles: number | null;
  // LR Direct specific
  targetRateMin: string;
  targetRateMax: string;
  bidDeadline: string;
  priorityTag: "standard" | "priority";
  shipperNotes: string;
  appointmentType: "fcfs" | "appointment";
  handlingRequirements: string[];
}

/** Capped bidding slots were removed from the form; DB column retained. */
const DEFAULT_MAX_BIDDERS = 5;

const defaultForm = (): DirectFormData => ({
  origin: "",
  destination: "",
  pickupEarliest: getLocalTodayYMD(),
  deliveryDate: "",
  pickHrs: "",
  dropHrs: "",
  loadType: "FTL",
  loadNature: "spot",
  equipmentType: "",
  length: "53",
  weight: "41000",
  commodity: "",
  referenceNumber: "",
  comments: "",
  totalMiles: null,
  targetRateMin: "",
  targetRateMax: "",
  bidDeadline: "",
  priorityTag: "standard",
  shipperNotes: "",
  appointmentType: "fcfs",
  handlingRequirements: [],
});

const createDefaultStops = (): Stop[] => [
  {
    id: crypto.randomUUID(),
    stop_number: 1,
    stop_type: "pickup",
    location: "",
    name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    latitude: null,
    longitude: null,
    scheduled_date: getLocalTodayYMD(),
  },
  {
    id: crypto.randomUUID(),
    stop_number: 2,
    stop_type: "delivery",
    location: "",
    name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    latitude: null,
    longitude: null,
    scheduled_date: "",
  },
];

/**
 * Map raw stop rows (from direct_load_stops, the dispatch RPC, or a template's
 * saved `stops` jsonb) into REAL form stops — full resolved locations with
 * numeric lat/lng, not display-only text. Used by edit, duplicate and template
 * prefill so nothing has to be retyped and every stop re-saves intact.
 */
const toFormStops = (rows: any[], opts?: { resetDates?: boolean }): Stop[] => {
  const num = (v: any) => (v === null || v === undefined || v === "" ? null : Number(v));
  const ordered = [
    ...rows.filter((s) => s.stop_type === "pickup"),
    ...rows.filter((s) => s.stop_type !== "pickup"),
  ];
  return ordered.map((s, i) => ({
    id: crypto.randomUUID(),
    stop_number: i + 1,
    stop_type: (s.stop_type === "pickup" ? "pickup" : "delivery") as Stop["stop_type"],
    location: s.city && s.state ? `${s.city}, ${s.state}` : (s.location || ""),
    name: s.name || "",
    address: s.address || "",
    city: s.city || "",
    state: s.state || "",
    postal_code: s.postal_code || "",
    country: s.country || "US",
    latitude: num(s.latitude),
    longitude: num(s.longitude),
    scheduled_date: opts?.resetDates
      ? (s.stop_type === "pickup" ? getLocalTodayYMD() : "")
      : (s.scheduled_date || (s.stop_type === "pickup" ? getLocalTodayYMD() : "")),
    pickup_hours: s.scheduled_time_start || s.pickup_hours || undefined,
    dropoff_hours: s.scheduled_time_end || s.dropoff_hours || undefined,
    instructions: s.instructions || undefined,
    pickup_number: s.pickup_number || undefined,
    pallet_count: s.pallet_count ?? null,
    confidential_notes: s.confidential_notes || undefined,
  }));
};



export default function LRDirectPostPage() {
  usePageMeta({
    title: "LR Direct — Post Load | LaneRunner",
    description: "Post a load to the LR Direct shipper-to-broker marketplace",
  });
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  useEffect(() => { showWarningOrRun(() => {}); }, [showWarningOrRun]);


  const { user, profile, loading, isAdmin } = useAuth();
  const { defaultEquipment, loaded: defaultEquipmentLoaded, saving: savingEquipmentDefault, justSaved: equipmentDefaultSaved, saveDefault: saveEquipmentDefault } = useDefaultEquipment();
  const isDemoUser = (profile as any)?.is_demo_user === true;
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: editLoadId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!editLoadId;
  const [editLoadAwarded, setEditLoadAwarded] = useState(false);
  const [editHasBids, setEditHasBids] = useState(false);
  const [editLoading, setEditLoading] = useState(isEditMode);

  const [form, setForm] = useState<DirectFormData>(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stops, setStops] = useState<Stop[]>(createDefaultStops);

  // Pre-fill equipment from the shipper's saved default (new posts only, once).
  const equipmentPrefilledRef = useRef(false);
  useEffect(() => {
    if (isEditMode || equipmentPrefilledRef.current) return;
    if (!defaultEquipmentLoaded || !defaultEquipment) return;
    equipmentPrefilledRef.current = true;
    setForm((prev) => (prev.equipmentType ? prev : { ...prev, equipmentType: defaultEquipment }));
  }, [defaultEquipment, defaultEquipmentLoaded, isEditMode]);

  // Track whether the shipper has manually edited the bid-deadline date.
  // Until they do, the deadline date auto-syncs to the pickup-earliest date.
  const bidDeadlineManuallyEditedRef = useRef(false);
  const [expandedStopId, setExpandedStopId] = useState<string | null>(null);
  const [privateNetwork, setPrivateNetwork] = useState<{ id: string; memberCount: number } | null>(null);
  const [postType, setPostType] = useState<"load" | "quote">(() => {
    // Pre-select QUOTE toggle when navigated from the start popup or legacy links via ?mode=quote
    // (also accepts legacy ?type=quote).
    const mode = searchParams.get("mode") ?? searchParams.get("type");
    return mode === "quote" ? "quote" : "load";
  });
  const [visibility, setVisibility] = useState<"open_market" | "private_only" | "both">(() => {
    const vis = searchParams.get("visibility");
    if (vis === "open_market" || vis === "private_only" || vis === "both") return vis;
    return "private_only";
  });
  // Optional prefill from ATLAS Broker Match: ?network=<id>&brokers=<id,id,...>
  const brokerMatchPrefill = useMemo(() => {
    if (typeof window === "undefined") return { networkId: null as string | null, brokerIds: null as string[] | null };
    const sp = new URLSearchParams(window.location.search);
    const brokers = (sp.get("brokers") || "").split(",").map((s) => s.trim()).filter(Boolean);
    return { networkId: sp.get("network"), brokerIds: brokers.length ? brokers : null };
  }, []);
  const [networkSelection, setNetworkSelection] = useState<NetworkBrokerSelection>({
    networkId: null,
    targetBrokerUserIds: null,
    isValid: false,
    selectedCount: 0,
    totalCount: 0,
  });

  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  // Optional LR Direct Load Brief (recorded audio) — isolated from marketplace briefs
  const [brief, setBrief] = useState<LRDirectLoadBriefValue | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [postingFromTemplateId, setPostingFromTemplateId] = useState<string | null>(null);

  // Auto-generate a template name from lane + equipment + type + visibility.
  const suggestedTemplateName = useMemo(() => {
    const firstPickup = stops.find((s) => s.stop_type === "pickup");
    const lastDrop = [...stops].reverse().find((s) => s.stop_type === "delivery");
    const o = firstPickup?.city && firstPickup?.state ? `${firstPickup.city}, ${firstPickup.state}` : null;
    const d = lastDrop?.city && lastDrop?.state ? `${lastDrop.city}, ${lastDrop.state}` : null;
    const lane = o && d ? `${o} → ${d}` : o || d || "New Lane";
    const parts: string[] = [lane];
    if (form.equipmentType) parts.push(form.equipmentType);
    parts.push(postType === "quote" ? "Quote" : "Load");
    if (visibility === "private_only") parts.push("Private");
    else if (visibility === "both") parts.push("Private + Open");
    return parts.join(" · ");
  }, [stops, form.equipmentType, postType, visibility]);


  // Auto-sync bid-deadline DATE to pickup-earliest date until the shipper
  // manually edits the deadline. Time portion defaults to 23:59 (end of day).
  useEffect(() => {
    if (bidDeadlineManuallyEditedRef.current) return;
    const pickup = form.pickupEarliest;
    if (!pickup) return;
    setForm((prev) => {
      const current = prev.bidDeadline || "";
      const [, t] = current.split("T");
      const time = (t || "23:59").slice(0, 5);
      const next = `${pickup}T${time}`;
      if (current === next) return prev;
      return { ...prev, bidDeadline: next };
    });
  }, [form.pickupEarliest]);


  // Load shipper's default private network + active member count (used for the "post to private" button label)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data: nets } = await supabase
        .from("shipper_private_networks" as any)
        .select("id, is_default")
        .eq("shipper_user_id", user.id);
      const net = ((nets as any[]) || []).find((n) => n.is_default) || (nets as any[])?.[0];
      if (!net || cancelled) { if (!cancelled) setPrivateNetwork(null); return; }
      const { count } = await supabase
        .from("shipper_network_brokers" as any)
        .select("id", { count: "exact", head: true })
        .eq("network_id", net.id)
        .eq("status", "accepted");
      if (!cancelled) setPrivateNetwork({ id: net.id, memberCount: count ?? 0 });
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Edit mode — load existing load + stops
  useEffect(() => {
    if (!isEditMode || !editLoadId || !user) return;
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      const { data: load, error } = await supabase
        .from("direct_loads" as any)
        .select("*")
        .eq("id", editLoadId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !load) {
        toast({ variant: "destructive", title: "Load not found", description: error?.message || "Unable to load this direct load." });
        navigate("/lr-direct/my-loads");
        return;
      }
      const l: any = load;
      if (l.awarded_bid_id) {
        toast({ variant: "destructive", title: "Cannot edit awarded load", description: "This load already has an awarded bid." });
        navigate("/lr-direct/my-loads");
        return;
      }
      if (l.deleted_at) {
        toast({ variant: "destructive", title: "Load deleted", description: "This load has been deleted." });
        navigate("/lr-direct/my-loads");
        return;
      }
      setEditLoadAwarded(false);
      const { count: bidCount } = await supabase
        .from("direct_bids")
        .select("id", { count: "exact", head: true })
        .eq("direct_load_id", editLoadId)
        .in("status", ["pending", "accepted"]);
      if (!cancelled) setEditHasBids((bidCount ?? 0) > 0);

      // Use RPC so the shipper-owner gets back the private dispatch fields.
      // Fallback to a direct table read so a failing/empty RPC can never
      // silently collapse a multi-stop load to origin + destination.
      let stopRows: any[] | null = null;
      const { data: rpcStops } = await supabase
        .rpc("get_direct_load_dispatch" as any, { p_load_id: editLoadId });
      if (rpcStops && (rpcStops as any[]).length > 0) {
        stopRows = rpcStops as any[];
      } else {
        const { data: rawStops } = await supabase
          .from("direct_load_stops" as any)
          .select("*")
          .eq("direct_load_id", editLoadId)
          .order("stop_number", { ascending: true });
        stopRows = (rawStops as any[]) || null;
      }



      if (cancelled) return;

      const originPD: PlaceData | undefined = l.origin_city ? {
        city: l.origin_city, state: l.origin_state,
        latitude: Number(l.origin_latitude) || 0, longitude: Number(l.origin_longitude) || 0,
        address: l.origin_address || "", name: l.origin_name || "",
        country: l.origin_country || "US", postal_code: l.origin_postal_code || "",
      } : undefined;
      const destPD: PlaceData | undefined = l.destination_city ? {
        city: l.destination_city, state: l.destination_state,
        latitude: Number(l.destination_latitude) || 0, longitude: Number(l.destination_longitude) || 0,
        address: l.destination_address || "", name: l.destination_name || "",
        country: l.destination_country || "US", postal_code: l.destination_postal_code || "",
      } : undefined;

      setForm({
        origin: originPD ? `${originPD.city}, ${originPD.state}` : "",
        originPlaceData: originPD,
        destination: destPD ? `${destPD.city}, ${destPD.state}` : "",
        destinationPlaceData: destPD,
        pickupEarliest: l.pickup_date || getLocalTodayYMD(),
        deliveryDate: l.delivery_date || "",
        pickHrs: "", dropHrs: "",
        loadType: (l.load_type || "FTL") as any,
        loadNature: ((l as any).load_nature || "spot") as any,
        equipmentType: l.equipment_type || "",
        length: l.length_ft ? String(l.length_ft) : "",
        weight: l.weight_lbs ? String(l.weight_lbs) : "",
        commodity: l.commodity || "",
        referenceNumber: l.reference_number || "",
        comments: l.special_instructions || "",
        totalMiles: l.total_miles ?? null,
        targetRateMin: l.target_rate_min != null ? String(l.target_rate_min) : "",
        targetRateMax: l.target_rate_max != null ? String(l.target_rate_max) : "",
        bidDeadline: l.bid_deadline ? new Date(l.bid_deadline).toISOString().slice(0, 16) : "",
        priorityTag: (l.priority_tag || "standard") as any,
        shipperNotes: l.shipper_notes_to_brokers || "",
        appointmentType: (l.appointment_type || "fcfs") as any,
        handlingRequirements: l.handling_requirements || [],
      });
      // In edit mode, preserve any existing bid deadline — don't auto-sync to pickup.
      if (l.bid_deadline) bidDeadlineManuallyEditedRef.current = true;


      if (stopRows && stopRows.length > 0) {
        setStops(toFormStops(stopRows));
      }

      setEditLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isEditMode, editLoadId, user, navigate, toast]);

  // Repost / duplicate — prefill the form from an existing quote or load
  // WITHOUT entering edit mode (submit creates a new record).
  useEffect(() => {
    if (isEditMode || !user) return;
    const sp = new URLSearchParams(window.location.search);
    const dupId = sp.get("duplicate");
    if (!dupId) return;
    let cancelled = false;
    (async () => {
      const { data: load } = await supabase
        .from("direct_loads" as any)
        .select("*")
        .eq("id", dupId)
        .maybeSingle();
      if (cancelled || !load) return;
      const l: any = load;
      const originPD: PlaceData | undefined = l.origin_city ? {
        city: l.origin_city, state: l.origin_state,
        latitude: Number(l.origin_latitude) || 0, longitude: Number(l.origin_longitude) || 0,
        address: l.origin_address || "", name: l.origin_name || "",
        country: l.origin_country || "US", postal_code: l.origin_postal_code || "",
      } : undefined;
      const destPD: PlaceData | undefined = l.destination_city ? {
        city: l.destination_city, state: l.destination_state,
        latitude: Number(l.destination_latitude) || 0, longitude: Number(l.destination_longitude) || 0,
        address: l.destination_address || "", name: l.destination_name || "",
        country: l.destination_country || "US", postal_code: l.destination_postal_code || "",
      } : undefined;
      setForm((prev) => ({
        ...prev,
        origin: originPD ? `${originPD.city}, ${originPD.state}` : prev.origin,
        originPlaceData: originPD ?? prev.originPlaceData,
        destination: destPD ? `${destPD.city}, ${destPD.state}` : prev.destination,
        destinationPlaceData: destPD ?? prev.destinationPlaceData,
        loadType: (l.load_type || prev.loadType) as any,
        loadNature: ((l as any).load_nature || prev.loadNature) as any,
        equipmentType: l.equipment_type || prev.equipmentType,
        length: l.length_ft ? String(l.length_ft) : prev.length,
        weight: l.weight_lbs ? String(l.weight_lbs) : prev.weight,
        commodity: l.commodity || prev.commodity,
        comments: l.special_instructions || prev.comments,
        totalMiles: l.total_miles ?? prev.totalMiles,
        targetRateMin: l.target_rate_min != null ? String(l.target_rate_min) : prev.targetRateMin,
        targetRateMax: l.target_rate_max != null ? String(l.target_rate_max) : prev.targetRateMax,
        priorityTag: (l.priority_tag || prev.priorityTag) as any,
        shipperNotes: l.shipper_notes_to_brokers || prev.shipperNotes,
        appointmentType: (l.appointment_type || prev.appointmentType) as any,
        handlingRequirements: l.handling_requirements || prev.handlingRequirements,
      }));
      // Carry over EVERY stop (all pickups + drops) as real resolved values,
      // so duplicating a multi-stop load doesn't collapse to origin + dest.
      const { data: dupStops } = await supabase
        .from("direct_load_stops" as any)
        .select("*")
        .eq("direct_load_id", dupId)
        .order("stop_number", { ascending: true });
      if (!cancelled && dupStops && (dupStops as any[]).length > 0) {
        setStops(toFormStops(dupStops as any[], { resetDates: true }));
      }
      // Exact duplicate — carry over the original's post type + audience.
      if (l.post_type === "quote" || l.post_type === "load") setPostType(l.post_type);
      if (l.visibility_mode === "open_market" || l.visibility_mode === "private_only" || l.visibility_mode === "both") {
        setVisibility(l.visibility_mode);
      }

    })();
    return () => { cancelled = true; };
  }, [isEditMode, user]);


  // Template prefill — ?tpl=<id> loads a saved template and fills the form
  // WITHOUT entering edit mode. Dates keep the normal defaults (pickup=today).
  useEffect(() => {
    if (isEditMode || !user) return;
    const sp = new URLSearchParams(window.location.search);
    const tplId = sp.get("tpl");
    if (!tplId) return;
    let cancelled = false;
    (async () => {
      const { data: tpl } = await supabase
        .from("lr_direct_load_templates" as any)
        .select("*")
        .eq("id", tplId)
        .maybeSingle();
      if (cancelled || !tpl) return;
      const t: any = tpl;
      const originPD: PlaceData | undefined = t.origin_city ? {
        city: t.origin_city, state: t.origin_state,
        latitude: Number(t.origin_latitude) || 0, longitude: Number(t.origin_longitude) || 0,
        address: t.origin_address || "", name: t.origin_name || "",
        country: t.origin_country || "US", postal_code: t.origin_postal_code || "",
      } : undefined;
      const destPD: PlaceData | undefined = t.destination_city ? {
        city: t.destination_city, state: t.destination_state,
        latitude: Number(t.destination_latitude) || 0, longitude: Number(t.destination_longitude) || 0,
        address: t.destination_address || "", name: t.destination_name || "",
        country: t.destination_country || "US", postal_code: t.destination_postal_code || "",
      } : undefined;
      setForm((prev) => ({
        ...prev,
        origin: originPD ? `${originPD.city}, ${originPD.state}` : prev.origin,
        originPlaceData: originPD ?? prev.originPlaceData,
        destination: destPD ? `${destPD.city}, ${destPD.state}` : prev.destination,
        destinationPlaceData: destPD ?? prev.destinationPlaceData,
        loadType: (t.load_type || prev.loadType) as any,
        loadNature: ((t as any).load_nature || prev.loadNature) as any,
        equipmentType: t.equipment_type || prev.equipmentType,
        length: t.length_ft ? String(t.length_ft) : prev.length,
        weight: t.weight_lbs ? String(t.weight_lbs) : prev.weight,
        commodity: t.commodity || prev.commodity,
        comments: t.special_instructions || prev.comments,
        targetRateMin: t.target_rate_min != null ? String(t.target_rate_min) : prev.targetRateMin,
        targetRateMax: t.target_rate_max != null ? String(t.target_rate_max) : prev.targetRateMax,
        priorityTag: (t.priority_tag || prev.priorityTag) as any,
        shipperNotes: t.shipper_notes || prev.shipperNotes,
        appointmentType: (t.appointment_type || prev.appointmentType) as any,
        handlingRequirements: t.handling_requirements || prev.handlingRequirements,
      }));
      if (t.post_type === "quote" || t.post_type === "load") setPostType(t.post_type);
      if (t.visibility_mode) setVisibility(t.visibility_mode);
      // Restore ALL stops the template saved (every pickup + drop, in order) as
      // real resolved values. Falls back to origin/dest for older templates
      // saved before multi-stop templates existed.
      const tplStops: any[] = Array.isArray(t.stops) ? t.stops : [];
      if (tplStops.length > 0) {
        setStops(toFormStops(tplStops, { resetDates: true }));
      } else if (originPD || destPD) {
        setStops((prev) => {
          const updated = [...prev];
          if (originPD) {
            const idx = updated.findIndex((s) => s.stop_type === "pickup");
            if (idx !== -1) updated[idx] = {
              ...updated[idx],
              location: `${originPD.city}, ${originPD.state}`,
              city: originPD.city, state: originPD.state,
              address: originPD.address, name: originPD.name,
              country: originPD.country, postal_code: originPD.postal_code,
              latitude: originPD.latitude, longitude: originPD.longitude,
            };
          }
          if (destPD) {
            const lastDelIdx = updated.map((s, i) => (s.stop_type === "delivery" ? i : -1)).filter((i) => i !== -1).pop();
            if (lastDelIdx !== undefined) updated[lastDelIdx] = {
              ...updated[lastDelIdx],
              location: `${destPD.city}, ${destPD.state}`,
              city: destPD.city, state: destPD.state,
              address: destPD.address, name: destPD.name,
              country: destPD.country, postal_code: destPD.postal_code,
              latitude: destPD.latitude, longitude: destPD.longitude,
            };
          }
          return updated;
        });
      }

      setPostingFromTemplateId(tplId);
    })();
    return () => { cancelled = true; };
  }, [isEditMode, user]);


  const updateField = useCallback(<K extends keyof DirectFormData>(key: K, value: DirectFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Route calculation ──
  useEffect(() => {
    const o = form.originPlaceData;
    const d = form.destinationPlaceData;
    if (!o?.latitude || !d?.latitude) return;
    getTruckRoute([o.longitude, o.latitude], [d.longitude, d.latitude])
      .then((r) => updateField("totalMiles", r ? Math.round(r.distance) : null))
      .catch(() => {});
  }, [form.originPlaceData, form.destinationPlaceData, updateField]);

  // ── Sync origin/dest from stops ──
  const syncFormFromStops = useCallback((currentStops: Stop[]) => {
    const firstPickup = currentStops.find((s) => s.stop_type === "pickup");
    const lastDelivery = [...currentStops].reverse().find((s) => s.stop_type === "delivery");

    setForm((prev) => {
      const updates: Partial<DirectFormData> = {};
      if (firstPickup?.city && firstPickup?.state) {
        updates.origin = `${firstPickup.city}, ${firstPickup.state}`;
        updates.originPlaceData = {
          city: firstPickup.city,
          state: firstPickup.state,
          latitude: firstPickup.latitude ?? 0,
          longitude: firstPickup.longitude ?? 0,
          address: firstPickup.address,
          name: firstPickup.name,
          country: firstPickup.country || "US",
          postal_code: firstPickup.postal_code || "",
        };
        if (firstPickup.scheduled_date) updates.pickupEarliest = firstPickup.scheduled_date;
      }
      if (lastDelivery?.city && lastDelivery?.state) {
        updates.destination = `${lastDelivery.city}, ${lastDelivery.state}`;
        updates.destinationPlaceData = {
          city: lastDelivery.city,
          state: lastDelivery.state,
          latitude: lastDelivery.latitude ?? 0,
          longitude: lastDelivery.longitude ?? 0,
          address: lastDelivery.address,
          name: lastDelivery.name,
          country: lastDelivery.country || "US",
          postal_code: lastDelivery.postal_code || "",
        };
        updates.deliveryDate = lastDelivery.scheduled_date || "";
      }
      return { ...prev, ...updates };
    });
  }, []);

  useEffect(() => {
    syncFormFromStops(stops);
  }, [stops, syncFormFromStops]);

  /**
   * The saved pickup/delivery dates come from the STOP rows, so the two date
   * fields must write back into the first pickup / last delivery stop. Editing
   * only `form` was silently discarded (the stop kept the old date, then the
   * stop→form sync restored it) — that's why edited dates never persisted.
   */
  const handlePickupEarliestChange = useCallback((v: string) => {
    updateField("pickupEarliest", v);
    setStops((prev) => {
      const idx = prev.findIndex((s) => s.stop_type === "pickup");
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], scheduled_date: v };
      return next;
    });
  }, [updateField]);

  const handleDeliveryDateChange = useCallback((v: string) => {
    updateField("deliveryDate", v);
    setStops((prev) => {
      let idx = -1;
      prev.forEach((s, i) => { if (s.stop_type === "delivery") idx = i; });
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], scheduled_date: v };
      return next;
    });
  }, [updateField]);


  // ── Stop management ──
  const handleAddStop = useCallback((type: "pickup" | "delivery") => {
    const newId = crypto.randomUUID();
    setStops((prev) => {
      const pickups = prev.filter((s) => s.stop_type === "pickup").length;
      const deliveries = prev.filter((s) => s.stop_type === "delivery").length;
      if (type === "pickup" && pickups >= 5) return prev;
      if (type === "delivery" && deliveries >= 5) return prev;
      if (prev.length >= 10) return prev;

      const newStop: Stop = {
        id: newId,
        stop_number: 0,
        stop_type: type,
        location: "",
        name: "",
        address: "",
        city: "",
        state: "",
        postal_code: "",
        country: "US",
        latitude: null,
        longitude: null,
        scheduled_date: type === "pickup" ? getLocalTodayYMD() : "",
      };

      let newStops: Stop[];
      if (type === "pickup") {
        const firstDeliveryIdx = prev.findIndex((s) => s.stop_type === "delivery");
        newStops = firstDeliveryIdx === -1 ? [...prev, newStop] : [...prev.slice(0, firstDeliveryIdx), newStop, ...prev.slice(firstDeliveryIdx)];
      } else {
        newStops = [...prev, newStop];
      }
      return newStops.map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
    setExpandedStopId(newId);
  }, []);

  const handleDeleteStop = useCallback((index: number) => {
    setStops((prev) => {
      if (prev.length <= 2) return prev;
      return prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
  }, []);

  const handleStopUpdate = useCallback((index: number, updatedStop: Stop) => {
    setStops((prev) => {
      const updated = [...prev];
      updated[index] = updatedStop;
      const pickups = updated.filter((s) => s.stop_type === "pickup");
      const deliveries = updated.filter((s) => s.stop_type === "delivery");
      return [...pickups, ...deliveries].map((s, i) => ({ ...s, stop_number: i + 1 }));
    });
  }, []);

  // Shipper-only gate (after all hooks) — admins bypass
  const userType = profile?.user_type;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!isAdmin && !isDemoUser && userType && userType !== "shipper") {
    return <Navigate to={userType === "broker" ? "/lr-direct/board" : "/broker-dashboard"} replace />;
  }

  const validate = (forPost: boolean, postTypeArg: "load" | "quote" = postType): boolean => {
    const e: Record<string, string> = {};
    if (!form.originPlaceData?.city) e.origin = "Origin required";
    if (!form.destinationPlaceData?.city) e.destination = "Destination required";
    if (!form.equipmentType) e.equipmentType = "Equipment required";
    if (!form.weight || parseFloat(form.weight) <= 0) e.weight = "Weight required";
    // Every entered stop must have a resolved city/state, otherwise it would be
    // silently dropped at save time (this is how added picks used to vanish).
    const incompleteStops = stops.filter((s) => !s.city || !s.state);
    if (incompleteStops.length > 0) {
      e.stops = `${incompleteStops.length} stop${incompleteStops.length === 1 ? "" : "s"} missing a location — pick a city for every pickup and drop`;
    }
    if (forPost && postTypeArg === "load" && !form.deliveryDate) {
      e.deliveryDate = "Delivery date is required on bid loads";
    }
    // Date-order sanity: the overall first pickup can't be after the last
    // delivery. Only checked when both dates exist (quotes may leave delivery
    // blank). Per-stop dates fall back to the main dates.
    const firstPickupDate =
      stops.find((s) => s.stop_type === "pickup")?.scheduled_date || form.pickupEarliest;
    const lastDeliveryDate =
      [...stops].reverse().find((s) => s.stop_type === "delivery")?.scheduled_date || form.deliveryDate;
    if (firstPickupDate && lastDeliveryDate && lastDeliveryDate < firstPickupDate) {
      e.deliveryDate = "Delivery date can't be before the pickup date";
    }
    if (forPost) {
      if (!form.bidDeadline) {
        e.bidDeadline = "Bid deadline is required";
      } else {
        const deadlineMs = new Date(form.bidDeadline).getTime();
        if (!Number.isFinite(deadlineMs) || deadlineMs <= Date.now() + 30 * 60 * 1000) {
          e.bidDeadline = "Bid deadline must be at least 30 minutes in the future";
        }
      }
    }

    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast({ variant: "destructive", title: "Missing required fields", description: Object.values(e).join(", ") });
      return false;
    }
    return true;
  };

  // ── Submit (save or publish) ──
  const handleSubmit = async (status: "draft" | "posted", visibilityArg: "open_market" | "private_only" | "both" = "open_market", postTypeArg: "load" | "quote" = "load") => {
    if (status === "posted" && !validate(true, postTypeArg)) return;

    if (status === "draft" && !form.originPlaceData?.city && !form.destinationPlaceData?.city) {
      toast({ variant: "destructive", title: "Add at least origin or destination to save a draft" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!user) throw new Error("Not authenticated");


      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: user.id });
      const companyId = userCompanies?.[0] || null;

      const o = form.originPlaceData;
      const d = form.destinationPlaceData;
      const firstPickupStop = stops.find((s) => s.stop_type === "pickup")!;
      const lastDeliveryStop = [...stops].reverse().find((s) => s.stop_type === "delivery");

      // Ensure total_miles is populated at submit — the reactive effect can
      // still be in-flight when the user clicks Post, leaving the field null
      // and breaking $/mi math, LanePredictor, and lane analytics downstream.
      // Multi-stop loads must report the SUMMED route across every stop in
      // order (origin → each extra pick → drop(s)), not just origin→final.
      const routeWaypoints: [number, number][] = [
        ...stops.filter((s) => s.stop_type === "pickup"),
        ...stops.filter((s) => s.stop_type === "delivery"),
      ]
        .filter((s) => s.city && s.state && s.latitude != null && s.longitude != null)
        .map((s) => [Number(s.longitude), Number(s.latitude)] as [number, number]);

      let resolvedTotalMiles: number | null = null;
      let milesSource: "od" | "multistop" | null = null;

      if (routeWaypoints.length > 2) {
        try {
          const r = await getMultiStopRoute(routeWaypoints);
          if (r?.distance) {
            resolvedTotalMiles = Math.round(r.distance);
            milesSource = "multistop";
          }
        } catch (err) {
          console.warn("[LRDirectPost] multi-stop mileage compute failed", err);
        }
      }

      if (!resolvedTotalMiles || resolvedTotalMiles <= 0) {
        resolvedTotalMiles = form.totalMiles ?? null;
        if (resolvedTotalMiles && resolvedTotalMiles > 0) milesSource = "od";
      }

      if (
        (!resolvedTotalMiles || resolvedTotalMiles <= 0) &&
        o?.latitude && o?.longitude && d?.latitude && d?.longitude
      ) {
        try {
          const r = await getTruckRoute(
            [o.longitude, o.latitude],
            [d.longitude, d.latitude],
          );
          if (r?.distance) {
            resolvedTotalMiles = Math.round(r.distance);
            milesSource = "od";
          }
        } catch (err) {
          console.warn("[LRDirectPost] mileage compute at submit failed", err);
        }
      }


      const payload: Record<string, any> = {
        load_number: form.referenceNumber.trim() || generateLoadNumber(),
        created_by: user.id,
        company_id: companyId,
        status,
        is_demo: isDemoUser,
        origin_name: o?.name || o?.city || "",
        origin_address: o?.address || (o ? `${o.city}, ${o.state}` : ""),
        origin_city: o?.city || "TBD",
        origin_state: o?.state || "XX",
        origin_country: o?.country || "US",
        origin_postal_code: o?.postal_code || null,
        origin_latitude: o?.latitude || null,
        origin_longitude: o?.longitude || null,
        destination_name: d?.name || d?.city || "",
        destination_address: d?.address || (d ? `${d.city}, ${d.state}` : ""),
        destination_city: d?.city || "TBD",
        destination_state: d?.state || "XX",
        destination_country: d?.country || "US",
        destination_postal_code: d?.postal_code || null,
        destination_latitude: d?.latitude || null,
        destination_longitude: d?.longitude || null,
        pickup_date: firstPickupStop?.scheduled_date || form.pickupEarliest || getLocalTodayYMD(),
        delivery_date: lastDeliveryStop?.scheduled_date || form.deliveryDate || null,
        equipment_type: form.equipmentType || "van",
        weight_lbs: parseFloat(form.weight) || null,
        length_ft: parseFloat(form.length) || null,
        load_type: form.loadType,
        load_nature: form.loadNature,
        commodity: form.commodity || null,
        special_instructions: [form.comments, form.pickHrs && `Pick Hrs: ${form.pickHrs}`, form.dropHrs && `Drop Hrs: ${form.dropHrs}`].filter(Boolean).join(" | ") || null,
        reference_number: form.referenceNumber.trim() || null,
        total_miles: resolvedTotalMiles,
        total_miles_source: milesSource,
        target_rate_min: null,
        target_rate_max: parseFloat(form.targetRateMax.replace(/,/g, "")) || null,
        // For private-network posts, every accepted broker should be able to bid — ignore the open-market cap.
        max_bidders: (visibilityArg === "private_only" || visibilityArg === "both")
          ? Math.max(networkSelection.selectedCount || privateNetwork?.memberCount || 0, DEFAULT_MAX_BIDDERS, 1)
          : DEFAULT_MAX_BIDDERS,
        bid_deadline: form.bidDeadline ? new Date(form.bidDeadline).toISOString() : null,
        priority_tag: form.priorityTag,
        shipper_notes_to_brokers: form.shipperNotes || null,
        posted_at: status === "posted" ? new Date().toISOString() : null,
        appointment_type: form.appointmentType,
        handling_requirements: form.handlingRequirements.length > 0 ? form.handlingRequirements : [],
        visibility_mode: visibilityArg,
        post_type: postTypeArg,
        private_network_id: (visibilityArg === "private_only" || visibilityArg === "both")
          ? (networkSelection.networkId ?? privateNetwork?.id ?? null)
          : null,
        target_broker_user_ids: (visibilityArg === "private_only" || visibilityArg === "both")
          ? (networkSelection.targetBrokerUserIds ?? null)
          : null,
      };

      // Build the stop rows BEFORE touching the DB so an empty/unhydrated stop
      // list can never wipe stops that are already saved on an edited load.
      // Numbering is re-derived here (picks first, then drops) so the sequence
      // is always origin → each extra pick → drop(s).
      const orderedStops = [
        ...stops.filter((s) => s.stop_type === "pickup"),
        ...stops.filter((s) => s.stop_type === "delivery"),
      ].filter((s) => s.city && s.state);

      // scheduled_date is NOT NULL in the DB — never emit null. Pickups fall
      // back to the form pickup date (then today); deliveries fall back to the
      // form delivery date, then the resolved pickup date.
      const fallbackPickupDate =
        firstPickupStop?.scheduled_date || form.pickupEarliest || getLocalTodayYMD();

      const buildStopRows = (id: string) =>
        orderedStops.map((s, i) => ({
          direct_load_id: id,
          stop_number: i + 1,
          stop_type: s.stop_type,
          name: s.name || "",
          address: s.address || "",
          city: s.city,
          state: s.state,
          postal_code: s.postal_code || null,
          country: s.country || "US",
          latitude: s.latitude,
          longitude: s.longitude,
          scheduled_date:
            s.scheduled_date ||
            (s.stop_type === "pickup" ? fallbackPickupDate : form.deliveryDate || fallbackPickupDate),
          scheduled_time_start: s.pickup_hours || null,
          scheduled_time_end: s.dropoff_hours || null,
          instructions: s.instructions || null,
        }));

      let loadId: string;
      if (isEditMode && editLoadId) {
        if (editLoadAwarded) throw new Error("Cannot edit an awarded load");
        // Don't overwrite posted_at on existing posted loads
        const updatePayload = { ...payload };
        delete updatePayload.created_by;
        delete updatePayload.company_id;
        delete updatePayload.is_demo;
        if (status === "posted") delete updatePayload.posted_at;
        const { error: updErr } = await supabase
          .from("direct_loads" as any)
          .update(updatePayload)
          .eq("id", editLoadId);
        if (updErr) throw updErr;
        loadId = editLoadId;

        // Replace stops — only when we actually have rows to write back.
        if (orderedStops.length > 0) {
          const { error: delErr } = await supabase
            .from("direct_load_stops" as any)
            .delete()
            .eq("direct_load_id", editLoadId);
          if (delErr) throw delErr;
          const { error: insErr } = await supabase
            .from("direct_load_stops" as any)
            .insert(buildStopRows(loadId));
          if (insErr) throw insErr;
        }
      } else {
        const { data: newLoad, error } = await supabase
          .from("direct_loads" as any)
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        // Guard: a returned row is the only proof the write landed. A silent
        // 0-row insert (RLS with_check mismatch) must surface, not fake success.
        if (!newLoad || !(newLoad as any).id) {
          throw new Error("The post was not saved. Please try again.");
        }
        loadId = (newLoad as any).id;

        // Persist every stop — a failure here must surface, not be swallowed,
        // otherwise multi-stop loads silently collapse to origin + destination.
        if (orderedStops.length > 0) {
          const { data: insertedStops, error: stopsErr } = await supabase
            .from("direct_load_stops" as any)
            .insert(buildStopRows(loadId))
            .select("id");
          if (stopsErr) throw stopsErr;
          if ((insertedStops?.length ?? 0) !== orderedStops.length) {
            throw new Error("Some stops did not save. Please re-open the load and check the stop list.");
          }
        }
      }


      // Optional: upload the LR Direct Load Brief audio (best-effort, never blocks the post).
      // Isolated data path: `direct-load-briefs` bucket + brief_* columns on direct_loads.
      if (brief && loadId) {
        try {
          const ext = brief.mime.includes("mp4") ? "mp4" : "webm";
          const briefPath = `${user.id}/${loadId}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from("direct-load-briefs")
            .upload(briefPath, brief.blob, { contentType: brief.mime, upsert: true });
          if (upErr) {
            toast({
              title: "Load posted",
              description: "Voice brief couldn't be saved — the load is live without it.",
            });
          } else {
            await supabase
              .from("direct_loads" as any)
              .update({
                brief_audio_path: briefPath,
                brief_mime_type: brief.mime,
                brief_duration_seconds: brief.durationSec,
              } as any)
              .eq("id", loadId);
          }
        } catch (briefErr) {
          console.error("[LRDirectPost] Load brief upload failed:", briefErr);
        }
      }

      // Save-as-template / bump usage of an existing template
      if (!isEditMode && status === "posted" && loadId) {
        try {
          if (postingFromTemplateId) {
            await bumpTemplateUsage(postingFromTemplateId);
          } else if (saveAsTemplate) {
            const laneFallback = [o?.city && o?.state ? `${o.city}, ${o.state}` : null,
                                 d?.city && d?.state ? `${d.city}, ${d.state}` : null].filter(Boolean).join(" → ");
            const nameToUse = (templateName.trim() || suggestedTemplateName || laneFallback || "Untitled template").slice(0, 80);
            const tplPayload: Record<string, any> = {
              shipper_user_id: user.id,
              name: nameToUse,
              origin_name: o?.name || null,
              origin_address: o?.address || null,
              origin_city: o?.city || null,
              origin_state: o?.state || null,
              origin_postal_code: o?.postal_code || null,
              origin_country: o?.country || null,
              origin_latitude: o?.latitude || null,
              origin_longitude: o?.longitude || null,
              destination_name: d?.name || null,
              destination_address: d?.address || null,
              destination_city: d?.city || null,
              destination_state: d?.state || null,
              destination_postal_code: d?.postal_code || null,
              destination_country: d?.country || null,
              destination_latitude: d?.latitude || null,
              destination_longitude: d?.longitude || null,
              equipment_type: form.equipmentType || null,
              weight_lbs: parseFloat(form.weight) || null,
              length_ft: parseFloat(form.length) || null,
              load_type: form.loadType,
              load_nature: form.loadNature,
              commodity: form.commodity || null,
              special_instructions: form.comments || null,
              shipper_notes: form.shipperNotes || null,
              handling_requirements: form.handlingRequirements || [],
              appointment_type: form.appointmentType,
              priority_tag: form.priorityTag,
              post_type: postTypeArg,
              visibility_mode: visibilityArg,
              private_network_id: (visibilityArg === "private_only" || visibilityArg === "both")
                ? (networkSelection.networkId ?? privateNetwork?.id ?? null) : null,
              max_bidders: DEFAULT_MAX_BIDDERS,
              target_rate_min: null,
              target_rate_max: parseFloat(form.targetRateMax.replace(/,/g, "")) || null,
              // Persist EVERY stop so a multi-stop template reposts intact.
              stops: orderedStops.map((s, i) => ({
                stop_number: i + 1,
                stop_type: s.stop_type,
                name: s.name || "",
                address: s.address || "",
                city: s.city,
                state: s.state,
                postal_code: s.postal_code || "",
                country: s.country || "US",
                latitude: s.latitude,
                longitude: s.longitude,
                pickup_hours: s.pickup_hours || null,
                dropoff_hours: s.dropoff_hours || null,
                instructions: s.instructions || null,
              })),
              usage_count: 1,

              last_used_at: new Date().toISOString(),
            };
            // Guard: check for both an error AND a returned row. A silent
            // 0-row insert (e.g. RLS with_check mismatch) must surface.
            const { data: tplRow, error: tplErr } = await supabase
              .from("lr_direct_load_templates" as any)
              .insert(tplPayload)
              .select("id")
              .maybeSingle();
            if (tplErr || !tplRow) {
              console.warn("[LRDirectPost] template save failed", tplErr, tplPayload);
              toast({
                title: "Template not saved",
                description: tplErr?.message || "The template row was not written. Your load posted successfully.",
                variant: "destructive",
              });
            } else {
              queryClient.invalidateQueries({ queryKey: ["lr-direct-load-templates"] });
              toast({ title: "Template saved", description: nameToUse });
            }
          }
        } catch (e: any) {
          console.warn("[LRDirectPost] template save/bump error", e);
          toast({ title: "Template not saved", description: e?.message || "Unknown error", variant: "destructive" });
        }
      }
      // Quote posts get their own fan-out (quote wording + broker quotes page).
      // This was never wired before, so quotes reached nobody.
      if (!isEditMode && status === "posted" && postTypeArg === "quote" && loadId) {
        supabase.functions.invoke("broadcast-new-quote-request", {
          body: { direct_load_id: loadId },
        }).catch((e) => console.warn("broadcast-new-quote-request failed", e));
      }
      if (!isEditMode && status === "posted" && postTypeArg === "load" && (visibilityArg === "private_only" || visibilityArg === "both") && loadId) {
        supabase.functions.invoke("broadcast-new-private-load", {
          body: { direct_load_id: loadId },
        }).catch((e) => console.warn("broadcast-new-private-load failed", e));
      }
      // Fan-out notifications to all opted-in brokers when a load hits the open marketplace.
      if (!isEditMode && status === "posted" && postTypeArg === "load" && (visibilityArg === "open_market" || visibilityArg === "both") && loadId) {
        supabase.functions.invoke("broadcast-new-open-load", {
          body: { direct_load_id: loadId },
        }).catch((e) => console.warn("broadcast-new-open-load failed", e));
      }

      // ATLAS Load Analysis — factual haul intelligence for bidding brokers.
      // Generated ONCE here at post time (and re-generated on edit) and cached on
      // the load row, so no broker page view ever triggers an AI call.
      // Best-effort: a failure stores nothing and the broker card simply hides.
      if (status === "posted" && postTypeArg === "load" && loadId) {
        supabase.functions.invoke("atlas-direct-load-analysis", {
          body: { direct_load_id: loadId, force: isEditMode },
        }).catch((e) => console.warn("atlas-direct-load-analysis failed", e));
      }


      const label = postTypeArg === "quote" ? "Quote" : "Load";
      toast({
        title: isEditMode
          ? `${label} updated`
          : status === "posted"
            ? (visibilityArg === "private_only"
                ? `Posted ${label.toLowerCase()} to your private network`
                : visibilityArg === "both"
                  ? `Posted ${label.toLowerCase()} to network + open market`
                  : `Published ${label.toLowerCase()} to LR Direct`)
            : "Draft saved",
        description: isEditMode
          ? (editHasBids ? "Brokers who have bid will see the updates." : "Your changes have been saved.")
          : status === "posted"
            ? (visibilityArg === "private_only"
                ? `Visible only to the ${privateNetwork?.memberCount ?? 0} broker(s) in your private network.`
                : visibilityArg === "both"
                  ? "Visible to your private network and the open marketplace."
                  : `Your ${label.toLowerCase()} is now live on the LR Direct marketplace.`)
            : "Your draft has been saved. You can publish it later.",
      });


      if (isEditMode) {
        navigate(postTypeArg === "quote" ? "/lr-direct/my-quotes" : "/lr-direct/my-loads");
      } else if (status === "posted") {
        // Quotes live on My Quotes; loads go to the Live Bid Feed.
        navigate(postTypeArg === "quote" ? "/lr-direct/my-quotes" : `/lr-direct/live/${loadId}`);
      } else {
        navigate(postTypeArg === "quote" ? "/lr-direct/my-quotes" : "/lr-direct/my-loads");
      }
    } catch (error: any) {
      console.error("LR Direct submit error:", error);
      toast({ variant: "destructive", title: "Error", description: error?.message || "An unexpected error occurred" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pickupCount = stops.filter((s) => s.stop_type === "pickup").length;
  const deliveryCount = stops.filter((s) => s.stop_type === "delivery").length;


  return (
    <div className="min-h-screen flex flex-col bg-background">
      <DirectPostHeader />
      {isEditMode && (
        <div className={`px-4 py-2 text-xs font-medium border-b border-border ${editHasBids ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-brand-teal/10 text-brand-teal"}`}>
          {editLoading
            ? "Loading load for editing…"
            : editHasBids
              ? "Editing posted load — brokers who have already bid will see your changes."
              : "Editing posted load."}
        </div>
      )}

      {/* Three-column layout (stacks on mobile, 2-col tablet, 3-col desktop) */}
      <div className="flex-1 w-full min-w-0 max-w-full px-3 sm:px-4 py-4 grid gap-4 items-start grid-cols-1 md:grid-cols-2 lg:[grid-template-columns:1fr_1fr_340px]">
        {/* LEFT COLUMN */}
        <div className="space-y-3 min-w-0">
          <ShipmentDetailsCard
            origin={form.origin}
            destination={form.destination}
            pickupEarliest={form.pickupEarliest}
            deliveryDate={form.deliveryDate}
            pickHrs={form.pickHrs}
            dropHrs={form.dropHrs}
            originCity={form.originPlaceData?.city}
            originState={form.originPlaceData?.state}
            destinationCity={form.destinationPlaceData?.city}
            destinationState={form.destinationPlaceData?.state}
            onOriginChange={(val, pd) => {
              updateField("origin", val);
              if (pd) {
                updateField("originPlaceData", pd);
                setErrors((prev) => { const n = { ...prev }; delete n.origin; return n; });
                setStops((prev) => {
                  const idx = prev.findIndex((s) => s.stop_type === "pickup");
                  if (idx === -1) return prev;
                  const updated = [...prev];
                  updated[idx] = {
                    ...updated[idx],
                    location: val,
                    city: pd.city,
                    state: pd.state,
                    address: pd.address || `${pd.city}, ${pd.state}`,
                    name: pd.name || pd.city,
                    country: pd.country || "US",
                    postal_code: pd.postal_code || "",
                    latitude: pd.latitude,
                    longitude: pd.longitude,
                  };
                  return updated;
                });
              }
            }}
            onDestinationChange={(val, pd) => {
              updateField("destination", val);
              if (pd) {
                updateField("destinationPlaceData", pd);
                setErrors((prev) => { const n = { ...prev }; delete n.destination; return n; });
                setStops((prev) => {
                  const lastDelIdx = prev.map((s, i) => (s.stop_type === "delivery" ? i : -1)).filter((i) => i !== -1).pop();
                  if (lastDelIdx === undefined) return prev;
                  const updated = [...prev];
                  updated[lastDelIdx] = {
                    ...updated[lastDelIdx],
                    location: val,
                    city: pd.city,
                    state: pd.state,
                    address: pd.address || `${pd.city}, ${pd.state}`,
                    name: pd.name || pd.city,
                    country: pd.country || "US",
                    postal_code: pd.postal_code || "",
                    latitude: pd.latitude,
                    longitude: pd.longitude,
                  };
                  return updated;
                });
              }
            }}
            onPickupEarliestChange={handlePickupEarliestChange}
            onDeliveryDateChange={handleDeliveryDateChange}
            deliveryDateRequired={postType === "load"}
            onPickHrsChange={(v) => updateField("pickHrs", v)}
            onDropHrsChange={(v) => updateField("dropHrs", v)}
            errors={errors}
          />
          <EquipmentDetailsCard
            loadType={form.loadType}
            loadNature={form.loadNature}
            equipmentType={form.equipmentType}
            length={form.length}
            weight={form.weight}
            commodity={form.commodity}
            referenceNumber={form.referenceNumber}
            comments={form.comments}
            onLoadTypeChange={(v) => updateField("loadType", v as any)}
            onLoadNatureChange={(v) => updateField("loadNature", v as any)}
            onEquipmentTypeChange={(v) => updateField("equipmentType", v)}
            onSaveEquipmentDefault={() => saveEquipmentDefault(form.equipmentType)}
            savingEquipmentDefault={savingEquipmentDefault}
            equipmentDefaultSaved={equipmentDefaultSaved}
            onLengthChange={(v) => updateField("length", v)}
            onWeightChange={(v) => updateField("weight", v)}
            onCommodityChange={(v) => updateField("commodity", v)}
            onReferenceNumberChange={(v) => updateField("referenceNumber", v)}
            onCommentsChange={(v) => updateField("comments", v)}
            totalMiles={form.totalMiles}
            errors={errors}
          />
          <LRDirectLoadBriefCard value={brief} onChange={setBrief} />
        </div>

        {/* MIDDLE COLUMN — Stops + Map */}
        <div className="space-y-3 min-w-0">
          <div className="rounded-lg border border-border bg-card p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                Pickups & Drops
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {pickupCount}p{deliveryCount}d
                </span>
              </h3>
              <span className="text-[10px] text-muted-foreground">{stops.length}/10 stops</span>
            </div>

            {stops.map((stop, index) => (
              <StopCard
                key={stop.id}
                stop={stop}
                index={index}
                canDelete={stops.length > 2}
                isExpanded={expandedStopId === stop.id}
                onToggleExpand={() => setExpandedStopId((prev) => (prev === stop.id ? null : stop.id))}
                onUpdate={(updated) => handleStopUpdate(index, updated)}
                onDelete={() => handleDeleteStop(index)}
              />
            ))}

            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => handleAddStop("pickup")} disabled={pickupCount >= 5 || stops.length >= 10} className="text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Pickup
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => handleAddStop("delivery")} disabled={deliveryCount >= 5 || stops.length >= 10} className="text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Drop
              </Button>
            </div>
          </div>

          <RouteMapCard stops={[]} formMode={null} />
        </div>

        {/* RIGHT COLUMN — LR Direct controls (spans full width on tablet) */}
        <div className="space-y-3 min-w-0 md:col-span-2 lg:col-span-1">
          {privateNetwork && (() => {
            const accentToken =
              postType === "quote"
                ? "--accent-quote"
                : visibility === "open_market"
                  ? "--accent-open"
                  : "--accent-private";
            return (
              <div
                className="rounded-xl border bg-card p-4 space-y-2 transition-colors"
                style={{
                  boxShadow: "var(--shadow-card)",
                  borderColor: `hsl(var(${accentToken}) / 0.55)`,
                  backgroundColor: `hsl(var(${accentToken}) / 0.03)`,
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Private network targeting
                  </div>
                  {(() => {
                    const meta = POST_TYPE_META[postType];
                    const Icon = meta.Icon;
                    return (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white shadow-sm flex-shrink-0"
                        style={{ background: meta.gradient }}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    );
                  })()}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Choose which brokers can see this load when you post privately. Open-market posting ignores this.
                </p>
                <NetworkBrokerSelector
                  userId={user?.id}
                  value={networkSelection}
                  onChange={setNetworkSelection}
                  preselectNetworkId={brokerMatchPrefill.networkId}
                  preselectBrokerUserIds={brokerMatchPrefill.brokerIds}
                />

              </div>
            );
          })()}

          <DirectPublishCard
            targetRateMin={form.targetRateMin}
            targetRateMax={form.targetRateMax}
            bidDeadline={form.bidDeadline}
            priorityTag={form.priorityTag}
            shipperNotes={form.shipperNotes}
            appointmentType={form.appointmentType}
            handlingRequirements={form.handlingRequirements}
            onTargetRateMinChange={(v) => updateField("targetRateMin", v)}
            onTargetRateMaxChange={(v) => updateField("targetRateMax", v)}
            onBidDeadlineChange={(v) => { bidDeadlineManuallyEditedRef.current = true; updateField("bidDeadline", v); if (errors.bidDeadline) setErrors((p) => { const n = { ...p }; delete n.bidDeadline; return n; }); }}
            bidDeadlineError={errors.bidDeadline}

            onPriorityTagChange={(v) => updateField("priorityTag", v)}
            onShipperNotesChange={(v) => updateField("shipperNotes", v)}
            onAppointmentTypeChange={(v) => updateField("appointmentType", v)}
            onHandlingRequirementsChange={(v) => updateField("handlingRequirements", v)}
            saveAsTemplate={saveAsTemplate}
            onSaveAsTemplateChange={setSaveAsTemplate}
            templateName={templateName}
            onTemplateNameChange={setTemplateName}
            templateAlreadySavedFromId={postingFromTemplateId}
            suggestedTemplateName={suggestedTemplateName}
            onPublish={(vis, pt) => {
              if ((vis === "private_only" || vis === "both")) {
                if (!privateNetwork || privateNetwork.memberCount === 0) {
                  toast({
                    title: "Set up your private network",
                    description: "Invite trusted brokers first, then post to them.",
                  });
                  navigate("/lr-direct/network-settings");
                  return;
                }
                if (!networkSelection.isValid) {
                  toast({
                    variant: "destructive",
                    title: "Pick at least one broker",
                    description: "Select brokers in the targeting card before posting to your network.",
                  });
                  return;
                }
              }
              handleSubmit("posted", vis, pt);
            }}
            postType={postType}
            onPostTypeChange={setPostType}
            visibility={visibility}
            onVisibilityChange={setVisibility}
            privateNetworkAvailable={!!privateNetwork && privateNetwork.memberCount > 0}
            privateNetworkMemberCount={networkSelection.selectedCount || (privateNetwork?.memberCount ?? 0)}
            isSubmitting={isSubmitting}
            totalMiles={form.totalMiles}
          />
        </div>
      </div>
      <VerificationWarningModal {...modalProps} />
    </div>
  );
}
